export const DECTA_TRANSACTIONS_ALLOWED_USER_IDS = [1, 2, 22, 256702875297201, 256160011575301, 267248184396201, 267248189118702, 267326084172805];
export const OVERVIEW_ANALYSIS_USER_IDS = [1, 22];
export const TRANSACTION_FAILURE_ANALYSIS_USER_IDS = [1, 2, 22];

export const canAnalyzeTransactionFailure = (userId: number | undefined, status: string | undefined): boolean =>
	userId !== undefined && TRANSACTION_FAILURE_ANALYSIS_USER_IDS.includes(userId) && ["failed", "error"].includes(status?.toLowerCase() ?? "");

export const isStatisticsSummaryOnly = (email: string | null | undefined): boolean => {
	const normalizedEmail = email?.trim().toLowerCase() ?? "";
	return normalizedEmail !== "andrejs.belskis@payswix.com" && /^[^@\s]+@payswix(?:\.com)?$/.test(normalizedEmail);
};

/**
 * Role ids are the stable part of the API contract. Names remain supported as a
 * fallback because older project/profile responses only contained `{ name }`.
 */
export const ROLE_IDS = {
	admin: 1,
	merchant: 2,
	aml: 3,
	accountant: 4,
	sales: 5,
	manager: 6,
	user: 7,
	support: 8,
} as const;

export type AppRole = keyof typeof ROLE_IDS;

export interface ProjectRole {
	id?: number | string;
	role_id?: number | string;
	name?: string;
	role?: string;
	description?: string;
}

export type Permission =
	| "nav.dashboard"
	| "nav.clients"
	| "nav.payouts"
	| "nav.reports"
	| "nav.providers"
	| "nav.users"
	| "nav.processing"
	| "nav.events"
	| "layout.sidebar"
	| "layout.financial_tools"
	| "transactions.view"
	| "reports.view"
	| "statements.view"
	| "clients.view_summary"
	| "clients.view_application"
	| "clients.create"
	| "clients.edit"
	| "clients.manage_settings"
	| "merchants.manage"
	| "merchants.manage_settings"
	| "features.admin";

const ROLE_PERMISSIONS: Record<AppRole, readonly Permission[]> = {
	admin: [
		"nav.dashboard",
		"nav.clients",
		"nav.payouts",
		"nav.reports",
		"nav.providers",
		"nav.users",
		"nav.processing",
		"nav.events",
		"layout.sidebar",
		"layout.financial_tools",
		"transactions.view",
		"reports.view",
		"statements.view",
		"clients.view_summary",
		"clients.view_application",
		"clients.create",
		"clients.edit",
		"clients.manage_settings",
		"merchants.manage",
		"merchants.manage_settings",
		"features.admin",
	],
	aml: [
		"nav.dashboard",
		"nav.clients",
		"layout.sidebar",
		"layout.financial_tools",
		"reports.view",
		"statements.view",
		"clients.view_application",
		"clients.edit",
		"clients.manage_settings",
		"merchants.manage",
		"merchants.manage_settings",
	],
	manager: [
		"nav.clients",
		"reports.view",
		"statements.view",
		"clients.view_application",
		"clients.edit",
		"clients.manage_settings",
		"merchants.manage",
		"merchants.manage_settings",
	],
	merchant: [
		"nav.clients",
		"layout.financial_tools",
		"transactions.view",
		"reports.view",
		"statements.view",
		"clients.view_summary",
		"clients.view_application",
		"merchants.manage",
		"merchants.manage_settings",
	],
	accountant: [
		"nav.dashboard",
		"nav.clients",
		"nav.payouts",
		"nav.reports",
		"layout.sidebar",
		"transactions.view",
		"reports.view",
		"statements.view",
		"clients.view_summary",
	],
	support: [
		"nav.dashboard",
		"nav.clients",
		"nav.events",
		"layout.sidebar",
		"transactions.view",
		"clients.view_summary",
	],
	// These roles are recognized by id, but their product access rules are not
	// defined yet. Keeping them empty avoids granting new access implicitly.
	sales: [],
	user: [],
};

const ROLE_BY_ID = Object.fromEntries(Object.entries(ROLE_IDS).map(([role, id]) => [id, role])) as Record<number, AppRole>;

const normalizeRoleName = (value: unknown): AppRole | null => {
	if (typeof value !== "string") return null;
	const normalized = value.trim().toLowerCase();
	return normalized in ROLE_PERMISSIONS ? (normalized as AppRole) : null;
};

export const getRoleName = (role: ProjectRole): AppRole | null => {
	const roleId = Number(role.id ?? role.role_id);
	if (Number.isFinite(roleId) && ROLE_BY_ID[roleId]) return ROLE_BY_ID[roleId];
	return normalizeRoleName(role.name ?? role.role);
};

export const hasRole = (roles: readonly ProjectRole[] | null | undefined, expected: AppRole): boolean =>
	roles?.some(role => getRoleName(role) === expected) ?? false;

export const hasAnyRole = (roles: readonly ProjectRole[] | null | undefined, expected: readonly AppRole[]): boolean =>
	expected.some(role => hasRole(roles, role));

export const hasPermission = (roles: readonly ProjectRole[] | null | undefined, permission: Permission): boolean =>
	roles?.some(role => {
		const roleName = getRoleName(role);
		return roleName ? ROLE_PERMISSIONS[roleName].includes(permission) : false;
	}) ?? false;
